import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Avatar,
  Grid,
  ButtonBase,
  CircularProgress,
} from "@mui/material";
import {
  Download,
  EventNote,
  Announcement,
  ChevronRight,
  PhoneOutlined,
  EmailOutlined,
  LocationOnOutlined,
  CalendarTodayOutlined,
  AccessTime,
  ReceiptLongOutlined,
  AssignmentOutlined,
  BeachAccessOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { BRAND } from "../../components/auth/authTheme";
import ProtectedImage from "../../components/ProtectedImage";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = BRAND.bg;

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "16px",
  border: `1px solid ${BRAND.border}`,
  boxShadow: "0 2px 12px rgba(26, 43, 74, 0.05)",
};

const ContactTile = ({ icon, label, value }) => {
  if (!value || value === "-") return null;
  return (
    <Box
      sx={{
        bgcolor: "#F8FAFC",
        borderRadius: "10px",
        border: `1px solid ${BRAND.border}`,
        p: 1,
        display: "flex",
        alignItems: "flex-start",
        gap: 0.75,
        minWidth: 0,
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "8px",
          bgcolor: BRAND.orangeLight,
          color: ORANGE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: "0.625rem",
            color: BRAND.muted,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 0.2,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: NAVY,
            fontWeight: 600,
            lineHeight: 1.35,
            wordBreak: label === "Email" ? "break-all" : "normal",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const HoursRing = ({ hoursLabel, progress, isActive }) => (
  <Box sx={{ position: "relative", width: 100, height: 100, mx: "auto" }}>
    <CircularProgress
      variant="determinate"
      value={100}
      size={100}
      thickness={3.5}
      sx={{ color: "#E8EDF3", position: "absolute", left: 0, top: 0 }}
    />
    <CircularProgress
      variant="determinate"
      value={progress}
      size={100}
      thickness={3.5}
      sx={{
        color: isActive ? "#22c55e" : "#CBD5E1",
        position: "absolute",
        left: 0,
        top: 0,
        "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography sx={{ fontSize: "0.625rem", color: BRAND.muted, fontWeight: 600, letterSpacing: "0.04em" }}>
        HOURS
      </Typography>
      <Typography
        sx={{
          fontSize: "1rem",
          fontWeight: 800,
          color: NAVY,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
        }}
      >
        {hoursLabel}
      </Typography>
    </Box>
  </Box>
);

const StatPill = ({ label, value, accent }) => (
  <Box
    sx={{
      flex: 1,
      textAlign: "center",
      py: 1,
      px: 0.5,
      borderRadius: "10px",
      bgcolor: accent ? BRAND.orangeLight : "#F1F5F9",
    }}
  >
    <Typography sx={{ fontSize: "1.125rem", fontWeight: 800, color: NAVY, lineHeight: 1.1 }}>
      {value ?? "-"}
    </Typography>
    <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted, fontWeight: 500, mt: 0.25 }}>
      {label}
    </Typography>
  </Box>
);

const ActionTile = ({ to, icon, label, color, bg }) => (
  <ButtonBase
    component={Link}
    to={to}
    sx={{
      ...cardSx,
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      py: 1.5,
      px: 1,
      textDecoration: "none",
      color: "inherit",
      minHeight: 88,
      transition: "transform 0.15s, box-shadow 0.15s",
      "&:active": { transform: "scale(0.97)" },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: bg,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: NAVY, textAlign: "center", lineHeight: 1.2 }}>
      {label}
    </Typography>
  </ButtonBase>
);

const getGreeting = (hour) => {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const EmployeeDashboardMobileView = ({
  userDetails,
  dashboardData,
  now,
  formatDuration,
  punchLoading = false,
  onSignIn,
  onSignOut,
  onApplyLeave,
  onDownloadPayslip,
}) => {
  const attendanceStatus = dashboardData?.attendance?.status;
  const canPunchIn =
    attendanceStatus === "not_signed_in" || attendanceStatus === "signed_out";
  const canPunchOut = attendanceStatus === "signed_in";
  const isSignedIn = attendanceStatus === "signed_in";

  const fullName = `${userDetails?.first_name || ""} ${userDetails?.last_name || ""}`.trim();
  const designation = userDetails?.profile_data?.designation_name || "Employee";
  const joiningDate = userDetails?.profile_data?.joining_date
    ? new Date(userDetails.profile_data.joining_date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  const reportOffice =
    userDetails?.profile_data?.report_office ||
    userDetails?.profile_data?.office_name ||
    userDetails?.profile_data?.branch_name ||
    userDetails?.profile_data?.city ||
    null;

  const productionHours = dashboardData?.attendance?.production_hours || "9:00";
  const nextHoliday = dashboardData?.holiday_data;
  const initials = `${userDetails?.first_name?.[0] || ""}${userDetails?.last_name?.[0] || ""}`.toUpperCase();

  const greeting = useMemo(() => getGreeting(now.getHours()), [now]);

  const dateTimeLabel = `${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}, ${now.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}`;

  const leaveBalance = dashboardData?.leave_balance;

  const hoursLabel = isSignedIn
    ? formatDuration(dashboardData?.attendance?.clock_in)
    : "00:00:00";

  const hoursProgress = useMemo(() => {
    if (!isSignedIn || !dashboardData?.attendance?.clock_in) return 0;
    const diffMs = now - new Date(dashboardData.attendance.clock_in);
    const elapsedHrs = diffMs / (1000 * 60 * 60);
    return Math.min(100, (elapsedHrs / 9) * 100);
  }, [isSignedIn, dashboardData?.attendance?.clock_in, now]);

  const contactItems = [
    { icon: <PhoneOutlined sx={{ fontSize: 15 }} />, label: "Phone", value: userDetails?.mobile_number },
    { icon: <EmailOutlined sx={{ fontSize: 15 }} />, label: "Email", value: userDetails?.email },
    { icon: <LocationOnOutlined sx={{ fontSize: 15 }} />, label: "Office", value: reportOffice },
    { icon: <CalendarTodayOutlined sx={{ fontSize: 15 }} />, label: "Joined", value: joiningDate },
  ].filter((item) => item.value && item.value !== "-");

  return (
    <Box
      className="employee-dashboard-mobile"
      sx={{
        pb: "calc(72px + env(safe-area-inset-bottom, 0px))",
        pt: 0.5,
        px: 1.5,
        bgcolor: BG,
        minHeight: "100%",
      }}
    >
      {/* ── Section 1: Profile ── */}
      <Box sx={{ ...cardSx, overflow: "hidden", mb: 1.25 }}>
        <Box
          sx={{
            background: `linear-gradient(145deg, ${NAVY} 0%, #243656 55%, ${ORANGE} 140%)`,
            px: 1.5,
            pt: 1.5,
            pb: 2,
            position: "relative",
          }}
        >
          <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.72)", fontWeight: 500, mb: 0.25 }}>
            {greeting}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.5 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#fff",
                color: ORANGE,
                fontWeight: 800,
                fontSize: "1.0625rem",
                border: "3px solid rgba(255,255,255,0.35)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              {userDetails?.profile_data?.profile_image ? (
                <ProtectedImage
                  path={userDetails.profile_data.profile_image}
                  alt={fullName}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                initials
              )}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "1.0625rem",
                  lineHeight: 1.2,
                  color: "#fff",
                }}
              >
                {fullName || "Employee"}
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: "rgba(255,255,255,0.88)", mt: 0.25 }}>
                {designation}
              </Typography>
              {userDetails?.employee_id && (
                <Box
                  sx={{
                    display: "inline-flex",
                    mt: 0.5,
                    px: 0.75,
                    py: 0.2,
                    borderRadius: "6px",
                    bgcolor: "rgba(255,255,255,0.18)",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.6875rem", color: "#fff", fontWeight: 700 }}>
                    {userDetails.employee_id}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {contactItems.length > 0 && (
          <Grid container spacing={1} sx={{ p: 1.25 }}>
            {contactItems.map((item) => (
              <Grid item xs={6} key={item.label}>
                <ContactTile icon={item.icon} label={item.label} value={item.value} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* ── Section 2: Attendance ── */}
      <Box sx={{ ...cardSx, overflow: "hidden", mb: 1.25 }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${BRAND.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#FAFBFC",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                bgcolor: BRAND.orangeLight,
                color: ORANGE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccessTime sx={{ fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.9375rem" }}>
              Today&apos;s Attendance
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted, fontWeight: 500, textAlign: "right", lineHeight: 1.3 }}>
            {dateTimeLabel}
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 1.5 }}>
          <HoursRing hoursLabel={hoursLabel} progress={hoursProgress} isActive={isSignedIn} />

          <Box sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 1.25 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.25,
                py: 0.45,
                borderRadius: "20px",
                bgcolor: isSignedIn ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)",
                border: `1px solid ${isSignedIn ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}`,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: isSignedIn ? "#22c55e" : "#ef4444",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: isSignedIn ? "#15803D" : "#B91C1C",
                }}
              >
                {dashboardData?.attendance?.clock_in
                  ? `Signed in at ${new Date(dashboardData.attendance.clock_in).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`
                  : "Not yet signed in"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: BRAND.muted,
                bgcolor: "#F1F5F9",
                px: 1.25,
                py: 0.4,
                borderRadius: "8px",
                fontWeight: 500,
              }}
            >
              Shift target · <strong style={{ color: NAVY }}>{productionHours} hrs</strong>
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              fullWidth
              disabled={punchLoading || !canPunchIn}
              onClick={onSignIn}
              sx={{
                bgcolor: BRAND.success,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderRadius: "10px",
                py: 1.1,
                boxShadow: "0 2px 8px rgba(22,163,74,0.25)",
                "&:hover": { bgcolor: BRAND.successHover, boxShadow: "0 4px 12px rgba(22,163,74,0.3)" },
                "&.Mui-disabled": { bgcolor: "#E5E7EB", color: BRAND.muted, boxShadow: "none" },
              }}
            >
              {punchLoading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Punch In"
              )}
            </Button>
            <Button
              variant="contained"
              fullWidth
              disabled={punchLoading || !canPunchOut}
              onClick={onSignOut}
              sx={{
                bgcolor: ORANGE,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderRadius: "10px",
                py: 1.1,
                boxShadow: "0 2px 8px rgba(230,90,0,0.2)",
                "&:hover": { bgcolor: BRAND.orangeHover, boxShadow: "0 4px 12px rgba(230,90,0,0.28)" },
                "&.Mui-disabled": { bgcolor: "#E5E7EB", color: BRAND.muted, boxShadow: "none" },
              }}
            >
              {punchLoading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Punch Out"
              )}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Leave balance — horizontal pills */}
      <Box sx={{ ...cardSx, p: 1.25, mb: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <EventNote sx={{ color: ORANGE, fontSize: "1.125rem" }} />
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.9375rem" }}>
              Leave Balance
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onApplyLeave}
            sx={{
              color: ORANGE,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.75rem",
              minWidth: 0,
              px: 0.5,
            }}
          >
            + Apply
          </Button>
        </Box>
        <Stack direction="row" spacing={0.75}>
          <StatPill label="Total" value={leaveBalance?.total_leave_allotted} />
          <StatPill label="Taken" value={leaveBalance?.total_leave_approved} accent />
          <StatPill label="Pending" value={leaveBalance?.total_leave_requested} />
        </Stack>
      </Box>

      {/* Quick actions 2x2 */}
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: NAVY, mb: 0.75, px: 0.25 }}>
        Quick Actions
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
        <ActionTile
          to="/employee_attendance"
          icon={<AccessTime sx={{ fontSize: 22 }} />}
          label="Attendance"
          color={ORANGE}
          bg={BRAND.orangeLight}
        />
        <ActionTile
          to="/employee_Payslips"
          icon={<ReceiptLongOutlined sx={{ fontSize: 22 }} />}
          label="Payslips"
          color="#2563EB"
          bg="rgba(37,99,235,0.08)"
        />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
        <ActionTile
          to="/leaves"
          icon={<BeachAccessOutlined sx={{ fontSize: 22 }} />}
          label="Leaves"
          color="#059669"
          bg="rgba(5,150,105,0.08)"
        />
        <ActionTile
          to="/employee_task_issue"
          icon={<AssignmentOutlined sx={{ fontSize: 22 }} />}
          label="Tasks"
          color="#7C3AED"
          bg="rgba(124,58,237,0.08)"
        />
      </Stack>

      {/* Latest payslip — compact row */}
      {dashboardData?.payslip && (
        <Box
          sx={{
            ...cardSx,
            p: 1.25,
            mb: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted, fontWeight: 600, textTransform: "uppercase" }}>
              Latest Payslip
            </Typography>
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.875rem" }}>
              {dashboardData.payslip.month || "-"}
              {dashboardData.payslip.net_salary && (
                <Typography component="span" sx={{ color: BRAND.muted, fontWeight: 500, ml: 0.75, fontSize: "0.8125rem" }}>
                  ₹{dashboardData.payslip.net_salary}
                </Typography>
              )}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download sx={{ fontSize: "1rem !important" }} />}
            onClick={onDownloadPayslip}
            sx={{
              borderColor: ORANGE,
              color: ORANGE,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              flexShrink: 0,
              "&:hover": { borderColor: ORANGE, bgcolor: BRAND.orangeLight },
            }}
          >
            PDF
          </Button>
        </Box>
      )}

      {/* Next holiday banner */}
      {nextHoliday?.occasion && (
        <Box
          sx={{
            background: `linear-gradient(90deg, ${BRAND.orangeLight} 0%, #fff 100%)`,
            borderRadius: "12px",
            border: `1px solid ${ORANGE}33`,
            px: 1.25,
            py: 1,
            mb: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.8125rem" }}>
              Upcoming Holiday
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: BRAND.muted }}>
              {nextHoliday.occasion.charAt(0).toUpperCase() + nextHoliday.occasion.slice(1)}
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 700, color: ORANGE, fontSize: "0.8125rem" }}>
            {new Date(nextHoliday.date).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            })}
          </Typography>
        </Box>
      )}

      {/* Announcements — compact list */}
      {dashboardData?.announcements?.length > 0 && (
        <Box sx={{ ...cardSx, p: 1.25, mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
            <Announcement sx={{ color: ORANGE, fontSize: "1.125rem" }} />
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.875rem" }}>
              Announcements
            </Typography>
          </Box>
          {dashboardData.announcements.slice(0, 2).map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 0.6,
                borderTop: index > 0 ? `1px solid ${BRAND.border}` : "none",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: BRAND.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  mr: 1,
                }}
              >
                {item.title}
              </Typography>
              <ChevronRight sx={{ color: BRAND.muted, fontSize: "1.125rem", flexShrink: 0 }} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EmployeeDashboardMobileView;
